// src/components/ContextBar.tsx (v2.3)
// 「今見ている条件」を全 chart 上部に sticky 表示
import { useFilters } from "../store";
import { fmtJst, ymJp } from "../lib/format";
import type { MetricRow } from "@shared/api";

export function ContextBar({ metrics, builtAt, forPrint = false }: { metrics: MetricRow[]; builtAt: string; forPrint?: boolean }) {
  const f = useFilters();
  const isSurgery = f.tab === "手術";
  const isCorr = f.tab === "相関分析";
  // 入院実績(前年度・予算比較) β: 対象月/表示形式/主要指標 は使わないので出さない (2026-07-10 ユーザ指示)。
  // 印刷時は DB 更新時刻も出さない。
  // [2026-09-07] 行為別売上 も同じ期間コントロール (開始月/終了月) に統一したので同じ扱い。
  const isCompare = f.tab === "入院実績(前年度・予算比較)" || f.tab === "行為別売上";
  const isKbAct = f.tab === "行為別売上";
  // [2026-09-18] 手術売上タブは共通の periodFrom/periodTo ではなく **専用の開始月/終了月 (opeFrom/opeTo)**
  //   で動く。既定分岐のまま出すと「対象月 / 表示形式 / 期間」に共通期間が並び、画面の数字と合わない
  //   条件が印刷に載る。専用の分岐を置く。
  const isOpeRp = f.tab === "手術売上";
  const opeFrom = f.opeFrom ?? f.asOfMonth;
  const opeTo = f.opeTo ?? f.asOfMonth;
  const kbActMetricLabel = f.kbActMetric === "手術" ? "手術(輸血除く)"
    : f.kbActMetric === "入院収入" ? (f.revenueBasis === "粗利" ? "薬剤材料費差引後入院収入 (総額)" : "入院収入 (総額)")
    : f.kbActMetric;
  const metricDef = metrics.find((m) => m["集計区分"] === f.metric);
  const corrSelCount = f.corrSel ? f.corrSel.split(",").filter(Boolean).length : 11;

  // forPrint: 印刷専用コピー (画面では非表示、 印刷時のみ表示)。 各印刷ページ先頭に条件を載せる用。
  const rootCls = forPrint
    ? "print-only items-center gap-x-2 gap-y-1 flex-wrap text-[10px] px-2 py-1 mb-1 border-b border-slate-300"
    : "sticky top-0 z-10 px-2 py-2 bg-white/95 backdrop-blur border-b border-slate-200 text-xs flex items-center gap-x-2 gap-y-1 flex-wrap no-print";

  return (
    <div className={rootCls}>
      {isCorr ? (
        <>
          <div className="flex items-center gap-1.5">
            <span className="text-slate-400">期間</span>
            <b className="text-slate-800">{f.corrFrom ? ymJp(f.corrFrom) : "直近1年"} 〜 {f.corrTo ? ymJp(f.corrTo) : "最新月"}</b>
            <span className="text-slate-400">(各月=1サンプル)</span>
          </div>
          <div className="text-slate-300">|</div>
          <div className="flex items-center gap-1.5">
            <span className="text-slate-400">診療科粒度</span>
            <b className="text-slate-700">{f.granularity}分類</b>
          </div>
          <div className="text-slate-300">|</div>
          <div className="flex items-center gap-1.5">
            <span className="text-slate-400">選択中</span>
            <b className="text-amber-700">{corrSelCount} 指標</b>
          </div>
          {f.focusedDept && (
            <>
              <div className="text-slate-300">|</div>
              <div className="flex items-center gap-1.5 bg-amber-50 px-2 py-0.5 rounded">
                <span className="text-amber-700 font-semibold">🎯 フォーカス</span>
                <b className="text-amber-800">{f.focusedDept}</b>
                <button onClick={() => f.setFocusedDept(null)}
                  className="text-amber-600 hover:text-red-600 text-xs">×</button>
              </div>
            </>
          )}
        </>
      ) : isOpeRp ? (
        <>
          <div className="flex items-center gap-1.5">
            <span className="text-slate-400">期間</span>
            <b className="text-slate-800">{ymJp(opeFrom)}〜{ymJp(opeTo)}</b>
          </div>
          <div className="text-slate-300">|</div>
          <div className="flex items-center gap-1.5">
            <span className="text-slate-400">表示指標</span>
            <b className="text-slate-700">{f.opeRevenueMode}</b>
          </div>
          <div className="text-slate-300">|</div>
          <div className="flex items-center gap-1.5">
            <span className="text-slate-400">診療科粒度</span>
            <b className="text-slate-700">{f.granularity}分類</b>
          </div>
          <div className="text-slate-300">|</div>
          <div className="flex items-center gap-1.5">
            <span className="text-slate-400">予定緊急</span>
            <b className="text-slate-700">{f.yotei}</b>
          </div>
          {f.opeProcedureQuery && (
            <>
              <div className="text-slate-300">|</div>
              <div className="flex items-center gap-1.5 bg-sky-50 px-2 py-0.5 rounded">
                <span className="text-sky-700 font-semibold">🔎 術式</span>
                <b className="text-sky-800">{f.opeProcedureQuery}</b>
              </div>
            </>
          )}
          {f.focusedDept && (
            <>
              <div className="text-slate-300">|</div>
              <div className="flex items-center gap-1.5 bg-amber-50 px-2 py-0.5 rounded">
                <span className="text-amber-700 font-semibold">🎯 フォーカス</span>
                <b className="text-amber-800">{f.focusedDept}</b>
                {!forPrint && (
                  <button onClick={() => f.setFocusedDept(null)}
                    className="text-amber-600 hover:text-red-600 text-xs">×</button>
                )}
              </div>
            </>
          )}
        </>
      ) : isCompare ? (
        <>
          <div className="flex items-center gap-1.5">
            <span className="text-slate-400">診療科粒度</span>
            <b className="text-slate-700">{f.granularity}分類</b>
          </div>
          <div className="text-slate-300">|</div>
          <div className="flex items-center gap-1.5">
            <span className="text-slate-400">医療区分</span>
            <b className="text-slate-700">{f.iryo}</b>
          </div>
          <div className="text-slate-300">|</div>
          <div className="flex items-center gap-1.5">
            <span className="text-slate-400">予定緊急</span>
            <b className="text-slate-700">{f.yotei}</b>
          </div>
          {isKbAct && (
            <>
              <div className="text-slate-300">|</div>
              <div className="flex items-center gap-1.5">
                <span className="text-slate-400">収入の基準</span>
                <b className="text-slate-700">{f.revenueBasis === "粗利" ? "薬剤材料費差引後" : "入院収入"}</b>
              </div>
              <div className="text-slate-300">|</div>
              <div className="flex items-center gap-1.5">
                <span className="text-slate-400">表示指標</span>
                <b className="text-slate-700">{kbActMetricLabel}</b>
              </div>
            </>
          )}
          {f.focusedDept && (
            <>
              <div className="text-slate-300">|</div>
              <div className="flex items-center gap-1.5 bg-amber-50 px-2 py-0.5 rounded">
                <span className="text-amber-700 font-semibold">🎯 フォーカス</span>
                <b className="text-amber-800">{f.focusedDept}</b>
                <button onClick={() => f.setFocusedDept(null)}
                  className="text-amber-600 hover:text-red-600 text-xs">×</button>
              </div>
            </>
          )}
        </>
      ) : (
      <>
      <div className="flex items-center gap-1.5">
        <span className="text-slate-400">対象月</span>
        <b className="text-slate-800 text-sm">{ymJp(f.asOfMonth)}</b>
      </div>
      <div className="text-slate-300">|</div>
      <div className="flex items-center gap-1.5">
        <span className="text-slate-400">表示形式</span>
        <b className="text-slate-700">{f.periodPreset}</b>
        <span className="text-slate-400">／ 期間 {ymJp(f.periodFrom)}〜{ymJp(f.periodTo)}</span>
      </div>
      {isSurgery ? (
        <>
          <div className="text-slate-300">|</div>
          <div className="flex items-center gap-1.5">
            <span className="text-slate-400">データ</span>
            <b className="text-slate-700">手術台帳 (ope_sera)</b>
          </div>
          <div className="text-slate-300">|</div>
          <div className="flex items-center gap-1.5">
            <span className="text-slate-400">予定緊急</span>
            <b className="text-slate-700">{f.yotei}</b>
          </div>
          {f.surgeryFocusedDept && (
            <>
              <div className="text-slate-300">|</div>
              <div className="flex items-center gap-1.5 bg-amber-50 px-2 py-0.5 rounded">
                <span className="text-amber-700 font-semibold">🎯 フォーカス</span>
                <b className="text-amber-800">{f.surgeryFocusedDept}</b>
                <button
                  onClick={() => f.setSurgeryFocusedDept(null)}
                  className="text-amber-600 hover:text-red-600 text-xs"
                >×</button>
              </div>
            </>
          )}
        </>
      ) : (
        <>
          <div className="text-slate-300">|</div>
          <div className="flex items-center gap-1.5">
            <span className="text-slate-400">診療科粒度</span>
            <b className="text-slate-700">{f.granularity}分類</b>
          </div>
          <div className="text-slate-300">|</div>
          <div className="flex items-center gap-1.5">
            <span className="text-slate-400">医療区分</span>
            <b className="text-slate-700">{f.iryo}</b>
          </div>
          <div className="text-slate-300">|</div>
          <div className="flex items-center gap-1.5">
            <span className="text-slate-400">予定緊急</span>
            <b className="text-slate-700">{f.yotei}</b>
          </div>
          <div className="text-slate-300">|</div>
          <div className="flex items-center gap-1.5">
            <span className="text-slate-400">主要指標</span>
            <b className="text-slate-700">{f.metric}</b>
            <span className="text-slate-400">({metricDef?.unit ?? ""})</span>
            {/* 「?」 tooltip は 2026-05-22 ユーザ判断で削除 */}
          </div>
          {f.focusedDept && (
            <>
              <div className="text-slate-300">|</div>
              <div className="flex items-center gap-1.5 bg-amber-50 px-2 py-0.5 rounded">
                <span className="text-amber-700 font-semibold">🎯 フォーカス</span>
                <b className="text-amber-800">{f.focusedDept}</b>
                <button
                  onClick={() => f.setFocusedDept(null)}
                  className="text-amber-600 hover:text-red-600 text-xs"
                >×</button>
              </div>
            </>
          )}
        </>
      )}
      </>
      )}
      {!(forPrint && isCompare) && (
        <div className="ml-auto flex items-center gap-2 text-[10px]">
          {/* 2026-05-22 本番運用化: PDCA メモ / verify badge / DB built 表示を非表示 */}
          <span className="text-slate-400">DB: {fmtJst(builtAt)}</span>
        </div>
      )}
    </div>
  );
}
